import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import path from 'path';
import config from '../../../config';
import Avatar from '../../user/Avatar';
import ChatBubble from './ChatBubble';
import ChatCurtain from './ChatCurtain';
import { getFileTypeImage } from '../../../util/fileTypeImages';

const url = new RegExp(
  /(https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b(?:[-a-zA-Z0-9@:%_+.~#?&//=]*))/
);
const img = new RegExp(/\.(?:png|jpg|jpeg|gif)$/);
const pdf = new RegExp(/\.(?:pdf)$/);

class ChatConversation extends PureComponent {
  constructor(props) {
    super(props);

    this.scrollToBottom = this.scrollToBottom.bind(this);
    this.handleWheel = this.handleWheel.bind(this);
  }

  /**
   * Scrolling event handler.  Used to determine if scrolled to the top or bottom.
   * @param {Object} event
   * @return {Boolean}
   */
  handleWheel(event) {
    const target = this.messageBody;

    const atTheTop = target.scrollTop === 0;
    const atTheBottom = target.offsetHeight + target.scrollTop === target.scrollHeight;
    const scrollingDown = event.deltaY > 0;
    const scrollingUp = event.deltaY < 0;

    if ((atTheBottom && scrollingDown) || (atTheTop && scrollingUp)) {
      event.stopPropagation();
      event.preventDefault();
    }

    return true;
  }

  /**
   * Constructs the appropriate date string based on the unix timestamp.
   * @param {Number} timestamp
   * @return {String}
   */
  buildDateString(timestamp, nextTimestamp) {
    if (!timestamp) {
      return null;
    }

    // Don't display timestamp if next is within 30 sec
    if (nextTimestamp) {
      if (nextTimestamp - timestamp <= 30000) {
        return null;
      }
    }

    return moment(timestamp).fromNow();
  }

  /**
   * Scrolls to the bottom of the conversation.
   * @return
   */
  scrollToBottom(smooth = true) {
    const options = smooth ? { behavior: 'smooth' } : {};

    if (this.messageEnd) {
      this.messageEnd.scrollIntoView(options);
    }
  }

  /**
   * Determine whether to chose the ChatCurtain component
   * @return {Boolean}
   */
  showCurtain() {
    const { status } = this.props;

    return status === 'Offline' || status === 'Busy';
  }

  render() {
    const { me, conversation, status, avatar } = this.props;

    if (!conversation.length) {
      return (
        <div className="chat-conversation">
          <ChatCurtain show={this.showCurtain()}>
            This user is currently <b>{status}</b> and may not receieve your message immediately.
          </ChatCurtain>
          <div className="body empty">
            <div className="empty-content">
              <p>
                <i className="zmdi zmdi-mood smile" />
                Begin your conversation by sending a message below
                <i className="zmdi zmdi-long-arrow-down arrow" />
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="chat-conversation">
        <ChatCurtain show={this.showCurtain()}>
          This user is currently <b>{status}</b> and may not receive your message immediately.
        </ChatCurtain>
        <div
          className="body"
          onWheel={this.handleWheel}
          ref={el => {
            this.messageBody = el;
          }}
        >
          <>
            {conversation.map((bubble, index) => {
              const nextBubble = conversation[index + 1];
              const nextTimestamp = nextBubble && nextBubble.id === bubble.id ? nextBubble.createdAt : null;

              return (
                <ChatBubble
                  key={index}
                  right={bubble.id === me}
                  left={bubble.id !== me}
                  timestamp={this.buildDateString(bubble.createdAt, nextTimestamp)}
                  hasAvatar={!!avatar}
                >
                  {avatar && bubble.id !== me && <Avatar user={{ avatar }} width={25} />}
                  {bubble.message.split(url).map((item, key) => {
                    if (img.test(item)) {
                      return (
                        <div key={key} className="placeholder-img">
                          <a href={item} target="_new">
                            <img src={item} alt="Shared" onLoad={this.scrollToBottom} />
                          </a>
                        </div>
                      );
                    } else if (pdf.test(item)) {
                      const fileIcon = getFileTypeImage(path.extname(item).substring(1));
                      return (
                        <div key={key} className="placeholder-file">
                          <a href={item} target="_new">
                            <img src={config.publicUrl(`img/${fileIcon}`)} alt="PDF" />
                          </a>
                        </div>
                      );
                    } else if (url.test(item)) {
                      return (
                        <span key={key}>
                          <a href={item} target="_new">
                            {item}
                          </a>
                        </span>
                      );
                    }

                    return <span key={key}>{item}</span>;
                  })}
                </ChatBubble>
              );
            })}
            <div
              style={{ float: 'left', clear: 'both' }}
              ref={el => {
                this.messageEnd = el;
              }}
            />
          </>
        </div>
      </div>
    );
  }
}

ChatConversation.defaultProps = {
  conversation: [],
};

ChatConversation.propTypes = {
  avatar: PropTypes.string,
  conversation: PropTypes.array,
  me: PropTypes.number,
  status: PropTypes.string,
};

export default ChatConversation;
